import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ChartFacade } from '@mode/charts/contract';
import { Flamingo, QueryBuilder } from '@mode/charts/util-vegas';
import {
  FeatureFlag,
  FeatureFlagsFacade,
  FileExportDomain,
  FlamingoResponse,
  FlamingoTypes,
} from '@mode/shared/contract-common';
import { FLAMINGO_RESERVED_REFS, isPresent } from '@mode/shared/util-js';
import { CsvExportEvents, recordEvent } from '@mode/shared/util-tracing';
import { DataViewFacade, FlamingoFacade } from '@mode/switch-views/contract';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { formatISO } from 'date-fns';
import * as FileSaver from 'file-saver';
import { EMPTY, merge, of, timer } from 'rxjs';
import {
  catchError,
  filter,
  first,
  groupBy,
  map,
  mergeMap,
  skip,
  switchMap,
  takeUntil,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import * as FileExportActions from './file-export.actions';
import * as FileExportSelectors from './file-export.selectors';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { ReportFilterFacade, ReportFilterHelpers } from '@mode/report-viewer/domain';
import { Model } from '@mode-switch/model';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { ViewDataModalFacade } from '@mode/reports/domain';

const RECONCILE_INTERVAL_MS = 15_000;

// SCAL-317800 (§9.2): per-export budget, size-scaled by input dataset size (floor / cap / per-GB slope).
const EXPORT_BUDGET_MIN_MS = 20 * 60 * 1000;
const EXPORT_BUDGET_MAX_MS = 90 * 60 * 1000;
const EXPORT_BUDGET_MS_PER_GB = 15 * 60 * 1000;
const EXPORT_BUDGET_EXCEEDED_MESSAGE =
  'This export is taking longer than expected. It may still finish — try again in a few minutes.';

@Injectable()
export class FileExportEffects {
  exportDesignerCsv$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FileExportActions.chartDesignerExportCsv),
      tap((action) => recordEvent(CsvExportEvents.designerCsvExportClick({ token: action.token }))),
      switchMap((action) => {
        return this.chartFacade.createCompleteFlamingoParams(action.token).pipe(
          switchMap(({ switchViewToken, encoding }) =>
            this.dataViewFacade.createDatasetBySwitchViewToken(switchViewToken).pipe(
              filter(isPresent),
              withLatestFrom(
                this.flamingoFacade
                  .createPlanIdFromRefId(action.refId)
                  .pipe(switchMap((planId) => this.flamingoFacade.createCountFromPlanId(planId)))
              ),
              map(([datasetResult, count]) => ({
                datasetResult,
                encoding,
                token: action.token,
                count,
              })),
              first()
            )
          ),
          withLatestFrom(
            this.dataViewFacade.model$,
            this.featureFlagFacade.featureValueAsObservable<number>(FeatureFlag.CsvExportRowLimit),
            this.chartFacade.createChartTitleFromVizToken(action.token),
            this.store.select(FileExportSelectors.selectAll),
            this.featureFlagFacade.asObservable(FeatureFlag.CsvExportAsyncCompletion)
          ),
          first()
        );
      }),
      map(([{ token, encoding, datasetResult, count }, model, limit, title, exports, asyncEnabled]) => {
        const filename = this.deduplicateFilename(title, exports);
        const plan = QueryBuilder.buildCsvQuery(model, datasetResult.queryToken, encoding, limit, 0);
        return FileExportActions.chartExportCsvPlan({
          token,
          refId: token + '-csv',
          plan,
          filename,
          queryRunToken: datasetResult.queryRunToken,
          switchViewToken: datasetResult.switchViewToken,
          rowCount: count ?? -1,
          datasetColumnCount: datasetResult.metaColumns.length,
          datasetRowCount: datasetResult.rowCount ?? -1,
          budgetMs: this.budgetMsFor(asyncEnabled, datasetResult.bytes),
        });
      }),
      tap((action) => {
        recordEvent(
          CsvExportEvents.generateDesignerCsvPlan({
            traceId: action.plan.id,
            chartToken: action.token,
            switchViewToken: action.switchViewToken,
            plan: action.plan,
            rowCount: action.rowCount,
            datasetColumnCount: action.datasetColumnCount,
            datasetRowCount: action.datasetRowCount,
          })
        );
      })
    )
  );

  exportViewDataCsv$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FileExportActions.viewDataChartExportCsv),
      withLatestFrom(
        this.viewDataModalFacade.title$,
        this.viewDataModalFacade.encoding$,
        this.viewDataModalFacade.queryToken$
      ),
      switchMap(([action, title, encoding, queryToken]) => {
        return this.dataViewFacade.createSwitchViewTokenByQueryToken(queryToken).pipe(
          filter(isPresent),
          switchMap((switchViewToken) =>
            this.dataViewFacade.createDatasetBySwitchViewToken(switchViewToken).pipe(
              filter(isPresent),
              withLatestFrom(
                this.flamingoFacade
                  .createPlanIdFromRefId(FLAMINGO_RESERVED_REFS.ViewData)
                  .pipe(switchMap((planId) => this.flamingoFacade.createCountFromPlanId(planId)))
              ),
              map(([datasetResult, count]) => ({
                title,
                datasetResult,
                encoding,
                count,
              })),
              first()
            )
          ),
          withLatestFrom(
            this.dataViewFacade.model$,
            this.featureFlagFacade.featureValueAsObservable<number>(FeatureFlag.CsvExportRowLimit),
            this.store.select(FileExportSelectors.selectAll),
            this.featureFlagFacade.asObservable(FeatureFlag.CsvExportAsyncCompletion)
          ),
          first()
        );
      }),
      map(([{ encoding, datasetResult, count, title }, model, limit, exports, asyncEnabled]) => {
        const filename = this.deduplicateFilename(title, exports);
        const plan = QueryBuilder.buildCsvQuery(model, datasetResult.queryToken, encoding, limit, 0);
        return FileExportActions.chartExportCsvPlan({
          token: datasetResult.queryToken, // There's not really a chart token
          refId: FLAMINGO_RESERVED_REFS.ViewData,
          plan,
          filename,
          queryRunToken: datasetResult.queryRunToken,
          switchViewToken: datasetResult.switchViewToken,
          rowCount: count ?? -1,
          datasetColumnCount: datasetResult.metaColumns.length,
          datasetRowCount: datasetResult.rowCount ?? -1,
          budgetMs: this.budgetMsFor(asyncEnabled, datasetResult.bytes),
        });
      }),
      tap((action) => {
        this.viewDataModalFacade.closeModal();
        recordEvent(
          CsvExportEvents.generateViewDataCsvPlan({
            traceId: action.plan.id,
            switchViewToken: action.switchViewToken,
            plan: action.plan,
            rowCount: action.rowCount,
            datasetColumnCount: action.datasetColumnCount,
            datasetRowCount: action.datasetRowCount,
          })
        );
      })
    )
  );

  exportReportCsv$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FileExportActions.chartReportExportCsv),
      tap((action) => recordEvent(CsvExportEvents.reportCsvExportClick({ token: action.token }))),
      switchMap((action) => {
        return this.chartFacade.createCompleteFlamingoParams(action.token).pipe(
          switchMap(({ switchViewToken, encoding }) =>
            this.dataViewFacade.createDatasetBySwitchViewToken(switchViewToken).pipe(
              filter(isPresent),
              withLatestFrom(
                this.flamingoFacade
                  .createPlanIdFromRefId(action.token)
                  .pipe(switchMap((planId) => this.flamingoFacade.createCountFromPlanId(planId)))
              ),
              map(([datasetResult, count]) => ({
                datasetResult,
                encoding,
                token: action.token,
                count,
              })),
              first()
            )
          ),
          withLatestFrom(
            this.dataViewFacade.model$,
            this.featureFlagFacade.featureValueAsObservable<number>(FeatureFlag.CsvExportRowLimit),
            this.chartFacade.createChartTitleFromVizToken(action.token),
            this.store.select(FileExportSelectors.selectAll),
            this.reportFilterFacade.filterControls$,
            this.featureFlagFacade.asObservable(FeatureFlag.CsvExportAsyncCompletion)
          ),
          first()
        );
      }),
      map(([{ token, encoding, datasetResult, count }, model, limit, title, exports, reportFilters, asyncEnabled]) => {
        const filename = this.deduplicateFilename(title, exports);
        const validFilters = Flamingo.getValidReportFilters(model, datasetResult.queryToken, reportFilters);
        encoding = ReportFilterHelpers.addReportFiltersToChartEncoding(
          Model.Views.find(model, datasetResult.queryToken),
          encoding,
          validFilters,
          false
        );
        const plan = QueryBuilder.buildCsvQuery(model, datasetResult.queryToken, encoding, limit, 0);

        return FileExportActions.chartExportCsvPlan({
          token,
          refId: token + '-csv',
          plan,
          filename,
          queryRunToken: datasetResult.queryRunToken,
          switchViewToken: datasetResult.switchViewToken,
          rowCount: count ?? -1,
          datasetColumnCount: datasetResult.metaColumns.length,
          datasetRowCount: datasetResult.rowCount ?? -1,
          budgetMs: this.budgetMsFor(asyncEnabled, datasetResult.bytes),
        });
      }),
      tap((action) => {
        recordEvent(
          CsvExportEvents.generateReportCsvPlan({
            traceId: action.plan.id,
            chartToken: action.token,
            switchViewToken: action.switchViewToken,
            datasetColumnCount: action.datasetColumnCount,
            datasetRowCount: action.datasetRowCount,
            rowCount: action.rowCount,
            plan: action.plan,
          })
        );
      })
    )
  );

  exportCsvPlan$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FileExportActions.chartExportCsvPlan, FileExportActions.retryChartExportPlan),
      tap((action) => {
        this.flamingoFacade.exportCsv(action.refId, action.plan, action.queryRunToken);
        recordEvent('helix-csv-export', 'dispatch-helix-query', {
          traceId: action.plan.id,
          chartToken: action.token,
          plan: action.plan,
        });
      }),
      mergeMap((action) => {
        const isRetry = action.type === FileExportActions.retryChartExportPlan.type;
        return this.flamingoFacade.createStateFromPlanId(action.plan.id).pipe(
          /**
           * Need to first result if retry since it'll return the status of the previous execution.
           * This is because NgRx actions don't execute synchronously when dispatched from a subscription.
           */
          skip(action.type === FileExportActions.retryChartExportPlan.type ? 1 : 0),
          tap((status) =>
            recordEvent('helix-csv-export', 'wait-plan-status', {
              traceId: action.plan.id,
              chartToken: action.token,
              plan: action.plan,
              status,
            })
          ),
          filter(
            (status) => status === FlamingoTypes.PlanStatus.Succeeded || status === FlamingoTypes.PlanStatus.Failed
          ),
          first(),
          switchMap((status) => {
            switch (status) {
              case FlamingoTypes.PlanStatus.Succeeded:
                return this.flamingoFacade.createExportFromPlanId(action.plan.id).pipe(
                  map((urlOrBlob) => {
                    return this.getExportSuccessAction(action)({
                      urlOrBlob,
                      id: action.plan.id,
                      token: action.token,
                      refId: action.refId,
                    });
                  }),
                  first(),
                  // A failure fetching the (already-terminal) result is NOT the poll timeout the outer
                  // catchError handles — surface it as a definite failure, not "still processing".
                  catchError((fetchErr) =>
                    of(
                      this.getExportFailedAction(action)({
                        id: action.plan.id,
                        refId: action.refId,
                        token: action.token,
                        error: fetchErr instanceof Error ? fetchErr : new Error(String(fetchErr)),
                      })
                    )
                  )
                );
              default:
                return this.flamingoFacade.createErrorFromPlanId(action.plan.id).pipe(
                  first(),
                  switchMap((errorResponse) => {
                    // SCAL-317800: the client stopped watching but the server is likely still running,
                    // so keep the row alive as Processing to re-attach.
                    if (this.isClientGaveUpResponse(errorResponse.type)) {
                      return this.featureFlagFacade.asObservable(FeatureFlag.CsvExportAsyncCompletion).pipe(
                        first(),
                        map((asyncEnabled) =>
                          asyncEnabled
                            ? FileExportActions.chartExportCsvStillProcessing({
                                id: action.plan.id,
                                token: action.token,
                                refId: action.refId,
                              })
                            : this.getExportFailedAction(action)({
                                id: action.plan.id,
                                refId: action.refId,
                                token: action.token,
                                error: new Error(errorResponse.type),
                              })
                        )
                      );
                    }

                    return of(
                      this.getExportFailedAction(action)({
                        id: action.plan.id,
                        refId: action.refId,
                        token: action.token,
                        error: new Error(errorResponse.type), // TODO: Decode this
                      })
                    );
                  }),
                  catchError((fetchErr) =>
                    of(
                      this.getExportFailedAction(action)({
                        id: action.plan.id,
                        refId: action.refId,
                        token: action.token,
                        error: fetchErr instanceof Error ? fetchErr : new Error(String(fetchErr)),
                      })
                    )
                  )
                );
            }
          }),
          // Defensive: an unexpected stream error becomes a definite failure, not a stuck Processing row.
          catchError((err) =>
            of(
              this.getExportFailedAction(action)({
                id: action.plan.id,
                refId: action.refId,
                token: action.token,
                error: err instanceof Error ? err : new Error(String(err)),
              })
            )
          )
        );
      })
    )
  );

  // SCAL-317800: while the drawer is open, re-attach in-flight (Processing) exports by re-dispatching
  // the idempotent export — on open and after each subsequent timeout — until they resolve or give up.
  reconcileProcessingExports$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FileExportActions.resumeProcessingExports),
      switchMap(() =>
        merge(
          // On open, immediately re-attach every export already sitting in Processing.
          this.store.select(FileExportSelectors.selectAll).pipe(
            first(),
            mergeMap((exports) => this.processingChartExports(exports)),
            map((exp) => {
              // Isolate one export's failure so it can't skip the rest of this batch (map runs
              // synchronously per item, but a thrown error terminates the whole shared stream).
              try {
                return this.reattachOrGiveUp(exp);
              } catch {
                return null;
              }
            }),
            filter(isPresent),
            catchError(() => EMPTY) // a selector/filter-level error must not tear down the loop
          ),
          // Each time an export (re-)enters Processing, wait a beat, then re-attach it if it is still
          // Processing. Grouped by export id with switchMap so at most ONE timer is alive per export:
          // ungrouped, two concurrent polls for the same export would each schedule a re-attach, and each
          // re-attach starts another poll — doubling the in-flight work every cycle.
          this.actions$.pipe(
            ofType(FileExportActions.chartExportCsvStillProcessing),
            groupBy((action) => action.id),
            mergeMap((byExportId$) =>
              byExportId$.pipe(
                switchMap((action) =>
                  timer(RECONCILE_INTERVAL_MS).pipe(
                    switchMap(() =>
                      this.store.select(FileExportSelectors.createExportSelector(action.id)).pipe(first())
                    ),
                    filter(
                      (exp): exp is FileExportDomain.ChartExport =>
                        isPresent(exp) &&
                        exp.status === FileExportDomain.ExportStatus.Processing &&
                        exp.type === FileExportDomain.ExportType.Chart
                    ),
                    map((exp) => this.reattachOrGiveUp(exp)),
                    catchError(() => EMPTY) // contain a per-export error so the loop keeps running
                  )
                )
              )
            )
          )
        ).pipe(takeUntil(this.actions$.pipe(ofType(FileExportActions.pauseProcessingExports))))
      )
    )
  );

  retryExport$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FileExportActions.retryExport),
      tap((action) =>
        recordEvent('helix-csv-export', 'retry-csv-export', {
          traceId: action.id,
        })
      ),
      switchMap((action) =>
        this.store.select(FileExportSelectors.createExportSelector(action.id)).pipe(
          filter(isPresent),
          first(),
          map((exp) => {
            switch (exp.type) {
              case FileExportDomain.ExportType.Chart: // Add logic here to support retries of different export types
                return FileExportActions.retryChartExportPlan({
                  plan: exp.plan,
                  queryRunToken: exp.queryRunToken,
                  refId: exp.refId || '', // This should be present at this point
                  token: exp.vizToken,
                });
              default:
                return FileExportActions.retryExportFailed({
                  id: exp.id,
                  error: new Error('Unsupported type: ' + exp.type),
                  token: '',
                  refId: exp.refId || '',
                });
            }
          })
        )
      )
    )
  );

  chartExportCsvSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(FileExportActions.chartExportCsvSuccess),
        tap((action) =>
          recordEvent(
            CsvExportEvents.csvExportSuccess({
              traceId: action.id,
              token: action.token,
              refId: action.refId,
              isRetry: false,
            })
          )
        )
      ),
    { dispatch: false }
  );

  chartExportCsvFailed$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(FileExportActions.chartExportCsvFailed),
        tap((action) =>
          recordEvent(
            CsvExportEvents.csvExportFailed({
              traceId: action.id,
              token: action.token,
              refId: action.refId,
              err: action.error,
              isRetry: false,
            })
          )
        )
      ),
    { dispatch: false }
  );

  // SCAL-317800: the timed-out-but-kept-alive path is the whole point of this feature, so make it
  // observable — otherwise it's invisible in telemetry (the row neither succeeded nor failed yet).
  chartExportCsvStillProcessing$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(FileExportActions.chartExportCsvStillProcessing),
        tap((action) =>
          recordEvent('helix-csv-export', 'csv-export-still-processing', {
            traceId: action.id,
            chartToken: action.token,
            refId: action.refId,
          })
        )
      ),
    { dispatch: false }
  );

  retryExportCsvSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(FileExportActions.retryExportSuccess),
        tap((action) =>
          recordEvent(
            CsvExportEvents.csvExportSuccess({
              traceId: action.id,
              token: action.token,
              refId: action.refId,
              isRetry: true,
            })
          )
        )
      ),
    { dispatch: false }
  );

  retryExportCsvFailed$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(FileExportActions.retryExportFailed),
        tap((action) =>
          recordEvent(
            CsvExportEvents.csvExportFailed({
              traceId: action.id,
              token: action.token,
              refId: action.refId,
              err: action.error,
              isRetry: true,
            })
          )
        )
      ),
    { dispatch: false }
  );

  downloadFile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FileExportActions.downloadExport),
      mergeMap((action) => this.store.select(FileExportSelectors.createExportSelector(action.id))),
      filter(isPresent),
      tap((action) =>
        recordEvent(
          CsvExportEvents.downloadClick({
            traceId: action.id,
          })
        )
      ),
      mergeMap((exp) => {
        const urlOrBlob = exp.urlOrBlob;
        if (urlOrBlob instanceof Blob) {
          FileSaver.saveAs(urlOrBlob, exp.fileName);
          return of(FileExportActions.downloadExportSuccess({ id: exp.id, downloadSize: urlOrBlob.size }));
        } else {
          // TODO: make successful export types to avoid urlOrBlob cast
          return this.http.get(urlOrBlob as string, { responseType: 'blob' }).pipe(
            tap((csv) => {
              FileSaver.saveAs(csv, exp.fileName);
            }),
            map((csv) => {
              return FileExportActions.downloadExportSuccess({ id: exp.id, downloadSize: csv.size });
            }),
            catchError((err) => {
              return of(FileExportActions.downloadExportFailed({ id: exp.id, error: err }));
            })
          );
        }
      })
    )
  );

  downloadExportSuccess$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(FileExportActions.downloadExportSuccess),
        tap((action) => {
          recordEvent(
            CsvExportEvents.downloadSuccess({
              traceId: action.id,
              downloadSize: action.downloadSize,
            })
          );
        })
      );
    },
    { dispatch: false }
  );

  downloadExportFailed$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(FileExportActions.downloadExportFailed),
        tap((action) =>
          recordEvent(
            CsvExportEvents.downloadFailed({
              traceId: action.id,
              err: action.error,
            })
          )
        )
      );
    },
    { dispatch: false }
  );

  // SCAL-317800: the client gave up (poll cap / unrecoverable 412) rather than the export failing.
  private isClientGaveUpResponse(type: FlamingoResponse.ResponseType): boolean {
    return (
      type === FlamingoResponse.ResponseType.TimeoutError ||
      type === FlamingoResponse.ResponseType.ClientSignatureExpired
    );
  }

  // SCAL-317800: size-scaled budget, clamped to [floor, cap]. Returns undefined if the budget is @ the floor
  // Fast exports get no estimate and give up at the floor via the `EXPORT_BUDGET_MIN_MS` fallback
  private budgetMsFor(asyncEnabled: boolean, datasetBytes: number | null | undefined): number | undefined {
    if (!asyncEnabled) {
      return undefined;
    }
    const gb = Math.max(0, datasetBytes ?? 0) / 1_000_000_000;
    const scaled = EXPORT_BUDGET_MIN_MS + gb * EXPORT_BUDGET_MS_PER_GB;
    const budgetMs = Math.min(EXPORT_BUDGET_MAX_MS, Math.max(EXPORT_BUDGET_MIN_MS, Math.round(scaled)));
    return budgetMs > EXPORT_BUDGET_MIN_MS ? budgetMs : undefined;
  }

  private processingChartExports(exports: FileExportDomain.FileExport[]): FileExportDomain.ChartExport[] {
    return exports.filter(
      (exp): exp is FileExportDomain.ChartExport =>
        exp.status === FileExportDomain.ExportStatus.Processing && exp.type === FileExportDomain.ExportType.Chart
    );
  }

  // SCAL-317800 (§9.2): re-attach within budget, else give up with a terminal error (recoverable via the cache-backed Retry).
  private reattachOrGiveUp(exp: FileExportDomain.ChartExport) {
    const elapsedMs = Date.now() - new Date(exp.createDate).getTime();
    const budgetMs = exp.budgetMs ?? EXPORT_BUDGET_MIN_MS;
    if (elapsedMs > budgetMs) {
      // Recorded separately from the generic failure event: giving up at the budget is a distinct
      // outcome from a server-reported failure, and its rate/elapsed time is how we tune the budget.
      recordEvent('helix-csv-export', 'csv-export-budget-exceeded', {
        traceId: exp.id,
        chartToken: exp.vizToken,
        refId: exp.refId || '',
        budgetMs,
        elapsedMs,
      });
      return FileExportActions.chartExportCsvFailed({
        id: exp.id,
        error: new Error(EXPORT_BUDGET_EXCEEDED_MESSAGE),
        token: exp.vizToken,
        refId: exp.refId || '',
      });
    }

    return FileExportActions.retryChartExportPlan({
      plan: exp.plan,
      queryRunToken: exp.queryRunToken,
      refId: exp.refId || '',
      token: exp.vizToken,
    });
  }

  private deduplicateFilename(title: Readonly<string | undefined>, exports: FileExportDomain.FileExport[]) {
    const baseFilename = `${title || 'Untitled Chart'} ${formatISO(new Date(), {
      representation: 'date',
    })}`;

    let filename = baseFilename + '.csv';
    for (let ii = 1; exports.some((exp) => exp.fileName === filename); ii++) {
      filename = baseFilename + ` (${ii}).csv`;
    }
    return filename;
  }

  private getExportSuccessAction(
    sourceAction:
      | ReturnType<typeof FileExportActions.chartExportCsvPlan>
      | ReturnType<typeof FileExportActions.retryChartExportPlan>
  ) {
    switch (sourceAction.type) {
      case FileExportActions.retryChartExportPlan.type:
        return FileExportActions.retryExportSuccess;
      case FileExportActions.chartExportCsvPlan.type:
        return FileExportActions.chartExportCsvSuccess;
    }
  }

  // Functions to support good action hygiene
  private getExportFailedAction(
    sourceAction:
      | ReturnType<typeof FileExportActions.chartExportCsvPlan>
      | ReturnType<typeof FileExportActions.retryChartExportPlan>
  ) {
    switch (sourceAction.type) {
      case FileExportActions.retryChartExportPlan.type:
        return FileExportActions.retryExportFailed;
      case FileExportActions.chartExportCsvPlan.type:
        return FileExportActions.chartExportCsvFailed;
    }
  }

  constructor(
    private actions$: Actions,
    private chartFacade: ChartFacade,
    private flamingoFacade: FlamingoFacade,
    private dataViewFacade: DataViewFacade,
    private featureFlagFacade: FeatureFlagsFacade,
    private http: HttpClient,
    private store: Store,
    private reportFilterFacade: ReportFilterFacade,
    private viewDataModalFacade: ViewDataModalFacade
  ) {}
}
